import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  UserSearch, HeartHandshake, BookOpen, TrendingUp, Shield, MessageCircle, BarChart3,
} from 'lucide-react';

const iconMap = {
  'user-search': UserSearch,
  'heart-handshake': HeartHandshake,
  'book-open': BookOpen,
  'trending-up': TrendingUp,
  'shield': Shield,
  'message-circle': MessageCircle,
  'ti-chart-bar': BarChart3,
};

// While a space is coming soon, Know Yourself carries its launch date; the rest just say "Coming soon".
// Once an admin flips is_coming_soon off in the Course Builder the pill disappears on its own.
export const spaceStatusLabel = (group) => {
  if (!group.is_coming_soon) return null;
  return group.name === 'Know Yourself' ? 'Opens Nov 1' : 'Coming soon';
};

/**
 * One space in the sidebar / mobile menu. Coming-soon spaces keep full colour but are not
 * interactive (a plain element, not a disabled button); admins can still open them to preview.
 */
const SpaceRow = ({ group, isActive, canOpen, onOpen, testIdPrefix = 'group', wrapText = false }) => {
  const Icon = iconMap[group.icon] || BookOpen;
  const label = spaceStatusLabel(group);
  const interactive = !group.is_coming_soon || canOpen;
  const rowRef = useRef(null);
  const [tip, setTip] = useState(null);
  const showTip = () => {
    const r = rowRef.current?.getBoundingClientRect();
    if (r) setTip({ top: r.top + r.height / 2, left: r.right + 12 });
  };
  const hideTip = () => setTip(null);
  // A fixed-position card would be left behind if the page scrolls under a still mouse
  useEffect(() => {
    if (!tip) return undefined;
    window.addEventListener('scroll', hideTip, true);
    return () => window.removeEventListener('scroll', hideTip, true);
  }, [tip]);

  const body = (
    <>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${group.color}20` }}
      >
        <Icon className="w-5 h-5" style={{ color: group.color }} aria-hidden="true" />
      </div>
      <div className="text-left flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium text-text-main ${wrapText ? '' : 'truncate'}`}>{group.name}</p>
          {label && (
            <span
              className="text-[10px] font-medium px-2 py-0.5 bg-secondary/15 text-secondary rounded-full flex-shrink-0"
              data-testid={`coming-soon-${group.id}`}
            >
              {label}
            </span>
          )}
        </div>
        <p className={`text-sm text-text-muted ${wrapText ? '' : 'truncate'}`}>{group.description}</p>
      </div>
    </>
  );

  const base = 'w-full flex items-center gap-3 p-4 rounded-arch-lg transition-all';

  if (!interactive) {
    // Not clickable, so on hover (desktop) show the full title and description in a card (drawn in a portal)
    return (
      <div
        ref={rowRef}
        role="group"
        aria-label={`${group.name}, ${label ? label.toLowerCase() : 'unavailable'}. ${group.description || ''}`}
        className={`${base} cursor-default`}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        data-testid={`${testIdPrefix}-${group.id}`}
      >
        {body}
        {tip && !wrapText && createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed z-[60] w-72 -translate-y-1/2 rounded-arch-lg p-4 shadow-lg border border-primary/20 bg-card"
            style={{ top: tip.top, left: tip.left }}
          >
            <p className="font-heading font-medium text-text-main mb-1">{group.name}</p>
            {label && <p className="text-xs font-medium text-secondary mb-2">{label}</p>}
            <p className="text-sm text-text-muted">{group.description}</p>
          </div>,
          document.body,
        )}
      </div>
    );
  }

  return (
    <motion.button
      type="button"
      whileHover={{ x: 4 }}
      onClick={() => onOpen(group)}
      className={`${base} ${isActive ? 'sidebar-active bg-primary/10' : 'hover:bg-primary/5'}`}
      data-testid={`${testIdPrefix}-${group.id}`}
    >
      {body}
    </motion.button>
  );
};

export default SpaceRow;
